/**
 * Entry Type on the client (#1936, #1938): the four values a line is stamped
 * with at save, mirrored from the Lambda layer's `entryType.js`. A guard test
 * pins the two lists together so the enum cannot drift.
 */
export const ENTRY_TYPES = Object.freeze(['spending', 'income', 'transfer', 'onetime']);
export const DEFAULT_ENTRY_TYPE = 'spending';
export const FUTURE_EXPENSES_BUDGET_TYPE = 2;

export const isEntryType = (value) => typeof value === 'string' && ENTRY_TYPES.includes(value);

/** Engine-string keys for the full word and the S / I / T / OT abbreviation. */
export const entryTypeLabelKey = (type) => `entryType.${type}`;
export const entryTypeAbbrKey = (type) => `entryType.abbr.${type}`;

/** Segmented options with full words (the in-place control, the part 7 flyout). */
export const entryTypeOptions = (t) =>
  ENTRY_TYPES.map((value) => ({ value, label: t[entryTypeLabelKey(value)] }));

/**
 * Segmented options abbreviated to S / I / T / OT, the full word on hover and
 * for assistive technology (the Payees page rows). Pass the theme's
 * `palette.entryType` to color each letter by its type, as the chips are.
 */
export const entryTypeAbbrOptions = (t, tones) =>
  ENTRY_TYPES.map((value) => ({
    value,
    label: t[entryTypeAbbrKey(value)],
    title: t[entryTypeLabelKey(value)],
    ariaLabel: t[entryTypeLabelKey(value)],
    ...(tones && tones[value] ? { tone: tones[value] } : {}),
  }));

/** Case-insensitive name lookup, the same rule the save handlers use. */
export const findPayeeByName = (payees, name) => {
  const wanted = (name || '').trim().toLowerCase();
  if (!wanted) return null;
  return (payees || []).find((p) => (p?.name || '').toLowerCase() === wanted) || null;
};

/** Fills `{name}` placeholders in an engine string; unknown names stay literal. */
export const interpolate = (template, vars = {}) =>
  String(template || '').replace(/\{(\w+)\}/g, (match, key) =>
    (Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match));

/** Where the {manage} link in a caption points: the Manage payees page. */
export const MANAGE_PAYEES_PATH = '/payees/cleanup';

/**
 * The live caption under the Entry Type control (#1938, copy revised #1943):
 * names what Save writes for the type shown. `prompt` is the hook's entry
 * ({ value, mode, payeeName }); null renders nothing. Returned as segments so
 * the renderer can turn `{manage}` into a link: `[{ text }, { manage: true,
 * text }]`. `entryTypePromptCaption` is the same caption as plain text.
 */
export const entryTypeCaptionSegments = (t, prompt) => {
  if (!prompt) return [];
  const vars = { type: t[entryTypeLabelKey(prompt.value)] };
  // A payee with a default: the pick is for this entry only, so the caption
  // says only what the line gets (owner, 2026-09-30).
  const template = prompt.mode === 'override' ? t['entryType.captionOverride'] : t['entryType.captionNoDefault'];
  const segments = [];
  String(template || '').split('{manage}').forEach((part, index) => {
    if (index > 0) segments.push({ manage: true, text: t['entryType.managePayees'] });
    if (part) segments.push({ text: interpolate(part, vars) });
  });
  return segments;
};

export const entryTypePromptCaption = (t, prompt) =>
  entryTypeCaptionSegments(t, prompt).map((seg) => seg.text).join('');

/**
 * The picker flyout's prompt for an option (#1943): what Save would write on
 * that payee. This entry's own choice when that payee is already the chosen
 * one, else the payee's default, else Spending.
 */
export const entryTypePromptForPayee = (payee, entry) => {
  if (!payee) return null;
  const hasDefault = isEntryType(payee.defaultEntryType);
  const own = entry && (entry.payeeName || '').trim().toLowerCase() === (payee.name || '').trim().toLowerCase();
  return {
    payeeName: payee.name,
    mode: hasDefault ? 'override' : 'noDefault',
    defaultType: hasDefault ? payee.defaultEntryType : null,
    value: own && isEntryType(entry.value) ? entry.value : (hasDefault ? payee.defaultEntryType : DEFAULT_ENTRY_TYPE),
  };
};
